import { assertContract, validateContent, validateI18n, validateSkin } from '@/core/release/contracts'
import type { PreviewMessage, PreviewReleasePayload, PreviewScene } from '@/domain/release/types'

export const PREVIEW_PROTOCOL = 'venue-audience-preview/v1' as const
const REQUIRED_DISABLED = ['network', 'telemetry', 'broadcast', 'externalNavigation', 'persistentCache'] as const
const SCENES: PreviewScene[] = ['home', 'entry', 'route', 'services', 'broadcast', 'annotation', 'vr']

export interface PreviewBridgeOptions {
  allowedOrigin: string
  parentWindow: Window
  onApply: (payload: PreviewReleasePayload) => void | Promise<void>
  onLocale?: (locale: 'zh-CN' | 'en-US') => void
  onScene?: (scene: PreviewScene) => void
}

export class PreviewBridge {
  private latestRevision = -1
  private highestReceivedRevision = -1
  private queue: Promise<void> = Promise.resolve()
  private listening = false
  private readonly listener = (event: MessageEvent<unknown>) => { void this.handleMessage(event) }

  constructor(private readonly options: PreviewBridgeOptions) {
    const parsed = new URL(options.allowedOrigin)
    if (!['https:', 'http:'].includes(parsed.protocol) || parsed.origin !== options.allowedOrigin) throw new Error('预览父页面来源必须是精确 HTTP(S) origin')
  }

  start() {
    if (this.listening) return
    window.addEventListener('message', this.listener)
    this.listening = true
    this.post('READY', requestId(), { capabilities: ['atomic-release-v1'] })
  }

  stop() { window.removeEventListener('message', this.listener); this.listening = false }

  async handleMessage(event: MessageEvent<unknown>) {
    if (event.origin !== this.options.allowedOrigin || event.source !== this.options.parentWindow) return
    const message = asMessage(event.data)
    if (!message) { this.post('VALIDATION_ERROR', messageRequestId(event.data), { errors: '预览消息格式无效' }); return }
    if (message.source !== 'venue-flow-ui' || message.target !== 'venue-h5-audience-next') return
    try {
      if (message.type === 'APPLY_PREVIEW_RELEASE') await this.apply(message)
      else if (message.type === 'SET_LOCALE') this.locale(message)
      else if (message.type === 'SET_SCENE') this.scene(message)
    } catch (error) {
      this.post('RUNTIME_ERROR', message.requestId, { code: 'PREVIEW_APPLY_FAILED', message: safeMessage(error) })
    }
  }

  private async apply(message: PreviewMessage) {
    const payload = clonePayload(message.payload)
    assertContract(validateContent, payload.content, 'AudienceContentV1')
    assertContract(validateSkin, payload.skin, 'ResolvedAudienceSkinV1')
    assertContract(validateI18n, payload.i18n, 'AudienceI18nV1')
    if (!REQUIRED_DISABLED.every((item) => payload.disabledCapabilities.includes(item))) throw new Error('预览必须禁用所有外部副作用能力')
    if (payload.previewRevision <= this.highestReceivedRevision) { this.post('APPLIED', message.requestId, { previewRevision: payload.previewRevision, ignored: true }); return }
    this.highestReceivedRevision = payload.previewRevision
    const task = this.queue.then(async () => {
      if (payload.previewRevision < this.highestReceivedRevision || payload.previewRevision <= this.latestRevision) {
        this.post('APPLIED', message.requestId, { previewRevision: payload.previewRevision, ignored: true }); return
      }
      await this.options.onApply(payload)
      this.latestRevision = payload.previewRevision
      this.post('APPLIED', message.requestId, { previewRevision: payload.previewRevision, ignored: false })
    })
    this.queue = task.catch(() => undefined)
    await task
  }

  private locale(message: PreviewMessage) {
    const locale = message.payload.locale
    if (locale !== 'zh-CN' && locale !== 'en-US') throw new Error('预览语言无效')
    this.options.onLocale?.(locale); this.post('APPLIED', message.requestId, { locale })
  }

  private scene(message: PreviewMessage) {
    const scene = message.payload.scene
    if (typeof scene !== 'string' || !SCENES.includes(scene as PreviewScene)) throw new Error('预览页面无效')
    this.options.onScene?.(scene as PreviewScene); this.post('APPLIED', message.requestId, { scene })
  }

  private post(type: PreviewMessage['type'], id: string, payload: Record<string, unknown>) {
    this.options.parentWindow.postMessage({ source: 'venue-h5-audience-next', target: 'venue-flow-ui', protocolVersion: PREVIEW_PROTOCOL, type, requestId: id, payload } satisfies PreviewMessage, this.options.allowedOrigin)
  }
}

export function isPreviewEntry(locationValue: Location = window.location): boolean {
  return locationValue.pathname.includes('/audience-next-preview/') || new URLSearchParams(locationValue.search).get('preview') === '1'
}

export function resolvePreviewOrigin(locationValue: Location = window.location, referrer = document.referrer): string | null {
  const configured = import.meta.env.VITE_AUDIENCE_PREVIEW_PARENT_ORIGIN as string | undefined
  let referrerOrigin = ''
  try { referrerOrigin = referrer ? new URL(referrer).origin : '' } catch { referrerOrigin = '' }
  const candidate = configured || new URLSearchParams(locationValue.search).get('previewOrigin') || referrerOrigin
  if (!candidate) return null
  try { const parsed = new URL(candidate); return parsed.origin === candidate && ['https:', 'http:'].includes(parsed.protocol) ? candidate : null } catch { return null }
}

function asMessage(value: unknown): PreviewMessage | null {
  if (!isRecord(value) || value.protocolVersion !== PREVIEW_PROTOCOL || typeof value.requestId !== 'string' || !value.requestId || value.requestId.length > 128 || !isRecord(value.payload)) return null
  if (typeof value.source !== 'string' || typeof value.target !== 'string' || typeof value.type !== 'string') return null
  return value as unknown as PreviewMessage
}
function clonePayload(value: Record<string, unknown>): PreviewReleasePayload {
  const cloned = JSON.parse(JSON.stringify(value)) as PreviewReleasePayload
  if (!Number.isInteger(cloned.previewRevision) || cloned.previewRevision < 0 || !SCENES.includes(cloned.scene) || !Array.isArray(cloned.disabledCapabilities)) throw new Error('预览发布载荷无效')
  return cloned
}
function messageRequestId(value: unknown) { return isRecord(value) && typeof value.requestId === 'string' ? value.requestId.slice(0, 128) : requestId() }
function requestId() { return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}` }
function safeMessage(error: unknown) { return (error instanceof Error ? error.message : '预览渲染失败').slice(0, 240) }
function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value) }
